import React, { useEffect, useCallback, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import SideBar from './SideBar';
import StoryContainer from './StoryContainer';
import ContentPanel from './ContentPanel';
import { useStory } from '../../../context/StoryContext';
import { useAudio } from '../../../context/AudioContext';
import { useStoryNavigation } from '../../../hooks/useStoryNavigation';
import { loadStoryData, saveStoryState, loadSavedState, getAudioPath, getLastBook, hasAudioSupport } from '../../../utils/storyUtils';
import { useBookData } from '../../../hooks/useBookData';

import { 
  trackNavigation,
  trackReadingProgress,
  logPageView,
  logEvent,
  trackTiming
} from '../../../config/analytics';

import { Button, Text } from "@geist-ui/core";
import { Layout } from "@geist-ui/icons";

const READER_STATE_KEY = 'om_play_reader_state';
const READER_GLOBAL_STATE_KEY = 'om_play_global_reader_state';

const StoryPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { bookData, loading, error } = useBookData(id || 'wolves_of_langston');
  const lastBook = getLastBook();
  
  // Use location state if available, otherwise use lastBook, finally fallback to wolves
  const bookInfo = location.state || lastBook || {
    id: 1,
    title: "The Wolves of Langston",
    tier: "Tier 1",
    cover: "/assets/wolves.png",
    author: "Daniel Howard",
    genre: "Murder Mystery"
  };

  const { title, tier, cover } = bookInfo;
  
  const { fontSize, isTwoColumns, handleFontSizeChange, handleColumns } = useStory();
  const { isPlaying, currentTime, duration, setIsPlaying, audioRef, voicePreference, setIsAudioLoaded } = useAudio();
  const [sidebarShow, setToggleSidebar] = React.useState(false);
  const [savedSceneId, setSavedSceneId] = React.useState(null);
  const [storyData, setStoryData] = React.useState(null);
  const [audioInitialized, setAudioInitialized] = useState(false);
  
  const {
    currentScene,
    currentChapter,
    loadScene: loadSceneFromHook,
    goBack,
  } = useStoryNavigation(id, storyData);

  // Check if the book has audio support
  const hasAudio = hasAudioSupport(id);

  const findSectionChapter = useCallback((sectionId) => {
    if (!storyData) return null;
    
    for (const [chapterName, chapter] of Object.entries(storyData)) {
      if (chapter[sectionId]) {
        return chapterName;
      }
    }
    return null;
  }, [storyData]);

  // Preload audio when the story loads
  const preloadAudio = useCallback((chapterName, sceneId, savedPosition = null) => {
    if (!hasAudio || !audioRef.current) return;
    
    const audioPath = getAudioPath(
      id,
      chapterName,
      sceneId,
      voicePreference
    );
    
    if (audioPath) {
      console.log(`[StoryView] Loading audio: ${audioPath} (Book: ${id}, Voice: ${voicePreference})`);
      audioRef.current.src = audioPath;
      audioRef.current.load();
      setIsAudioLoaded(false);
      
      // Add event listener to notify when audio is loaded
      const handleCanPlayThrough = () => {
        console.log(`[StoryView] Audio loaded: ${audioPath}`);
        setIsAudioLoaded(true);
        
        // If we have a saved position, set the audio currentTime
        if (savedPosition !== null && !isNaN(savedPosition)) {
          console.log('Restoring audio position to:', savedPosition);
          try {
            audioRef.current.currentTime = savedPosition;
            
            // Double-check that the position was set correctly
            setTimeout(() => {
              if (Math.abs(audioRef.current.currentTime - savedPosition) > 1) {
                console.log('Position not set correctly, trying again:', savedPosition);
                audioRef.current.currentTime = savedPosition;
              }
            }, 100);
          } catch (error) {
            console.error('Error setting audio position:', error);
          }
        }
        
        audioRef.current.removeEventListener('canplaythrough', handleCanPlayThrough);
      };
      
      audioRef.current.addEventListener('canplaythrough', handleCanPlayThrough);
      
      // Store the saved position in a ref for later use
      if (savedPosition !== null && !isNaN(savedPosition)) {
        audioRef.current.dataset.savedPosition = savedPosition;
      }
    }
  }, [id, hasAudio, audioRef, voicePreference, setIsAudioLoaded]);

  // Add a separate effect specifically to restore audio position
  useEffect(() => {
    if (!hasAudio || !currentScene || !currentChapter || !audioRef.current) return;
    
    // Try to get saved audio position from localStorage
    let savedPosition = null;
    try {
      const readerState = localStorage.getItem(READER_STATE_KEY);
      if (readerState) {
        const state = JSON.parse(readerState);
        if (state.bookId === id && state.chapter === currentChapter && state.sceneId === currentScene.sceneId && state.audioTime) {
          savedPosition = state.audioTime;
        }
      }
    } catch (error) {
      console.error("Error getting saved audio position:", error);
    }
    
    // If we have a saved position, try to set it multiple ways
    if (savedPosition !== null && !isNaN(savedPosition)) {
      console.log('Trying to restore audio position in direct effect:', savedPosition);

      // Try to set it directly
      try {
        audioRef.current.currentTime = savedPosition;
      } catch (error) {
        console.warn('Initial position setting failed, will try on canplay event');
      }
      
      // Also set up an event listener to set it when the audio can play
      const handleCanPlay = () => {
        console.log('Restoring position on canplay event:', savedPosition);
        try {
          audioRef.current.currentTime = savedPosition;
        } catch (error) {
          console.error('Error setting position on canplay:', error);
        }
        audioRef.current.removeEventListener('canplay', handleCanPlay);
      };
      
      audioRef.current.addEventListener('canplay', handleCanPlay);

      // As a fallback, try again after a short delay
      setTimeout(() => {
        if (audioRef.current) {
          try {
            audioRef.current.currentTime = savedPosition;
          } catch (error) {
            console.error('Error setting position after timeout:', error);
          }
        }
      }, 500);
      
      // Clean up event listener
      return () => {
        if (audioRef.current) {
          audioRef.current.removeEventListener('canplay', handleCanPlay);
        }
      };
    }
  }, [hasAudio, currentScene, currentChapter, id, audioRef]);

  useEffect(() => {
    if (currentScene && currentChapter && hasAudio && audioRef.current) {
      
      try {
        const readerState = localStorage.getItem(READER_STATE_KEY);
        if (readerState) {
          const state = JSON.parse(readerState);
          
          if (state.bookId === id && state.chapter === currentChapter && state.sceneId === currentScene.sceneId) {
            console.log('Found matching reader state for current scene');
            
            if (state.audioTime && !isNaN(state.audioTime)) {
              const applyPosition = (position) => {
                
                try {
                  audioRef.current.currentTime = position;
                } catch (e) {
                  console.warn('Error directly setting audio position:', e);
                }
                
                [100, 300, 600, 1000].forEach(delay => {
                  setTimeout(() => {
                    if (audioRef.current) {
                      try {
                        if (Math.abs(audioRef.current.currentTime - position) > 1) {
                          audioRef.current.currentTime = position;
                        }
                      } catch (e) {
                        console.error(`Error setting position after ${delay}ms:`, e);
                      }
                    }
                  }, delay);
                });
              };
              
              const handleAudioReady = () => {
                applyPosition(state.audioTime);
                audioRef.current.removeEventListener('canplaythrough', handleAudioReady);
                audioRef.current.removeEventListener('loadedmetadata', handleAudioReady);
              };
              
              audioRef.current.addEventListener('canplaythrough', handleAudioReady);
              audioRef.current.addEventListener('loadedmetadata', handleAudioReady);
              
              applyPosition(state.audioTime);
              
              return () => {
                if (audioRef.current) {
                  audioRef.current.removeEventListener('canplaythrough', handleAudioReady);
                  audioRef.current.removeEventListener('loadedmetadata', handleAudioReady);
                }
              };
            }
          }
        }
      } catch (error) {
        console.error('Error restoring audio time from reader state:', error);
      }
    }
  }, [currentScene, currentChapter, hasAudio, id, audioRef]);

  useEffect(() => {
    const initializeStory = async () => {
      const startTime = performance.now();
      
      if (bookData) {
        setStoryData(bookData);
        
        // Track book view with detailed info
        logPageView(`Reading: ${title}`, id);
        logEvent('Book', 'Open', title, id);

        // Stop any playing audio when loading a new book
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }

        if (location.hash) {
          const sectionNumber = location.hash.replace('#', '');
          if (sectionNumber && !isNaN(parseInt(sectionNumber, 10))) {
            let targetChapter = 'Chapter 1';
            
            for (const [chapterName, chapter] of Object.entries(bookData)) {
              if (chapter[sectionNumber]) {
                targetChapter = chapterName;
                break;
              }
            }
            
            loadSceneFromHook(targetChapter, sectionNumber);
            
            let savedPosition = null;
            try {
              const readerState = localStorage.getItem(READER_STATE_KEY);
              if (readerState) {
                const state = JSON.parse(readerState);
                if (state.bookId === id && state.chapter === targetChapter && state.sceneId === sectionNumber && state.audioTime) {
                  savedPosition = state.audioTime;
                }
              }
            } catch (error) {
              console.error("Error getting saved audio position:", error);
            }
            
            if (hasAudio) {
              preloadAudio(targetChapter, sectionNumber, savedPosition);
            }
          }
        } 
        else {
          try {
            console.log("Looking for reader state in localStorage");
            const readerState = localStorage.getItem(READER_STATE_KEY);
            
            if (readerState) {
              const state = JSON.parse(readerState);
              console.log("Found reader state:", state);
              
              if (state.bookId === id) {
                loadSceneFromHook(state.chapter, state.sceneId);
                
                if (hasAudio && state.audioTime) {
                  preloadAudio(state.chapter, state.sceneId, state.audioTime);
                }
                
                return; // Exit early if we've loaded from reader state
              }
            }
            
            const savedState = loadSavedState(id);
            if (savedState) {
              loadSceneFromHook(savedState.chapter, savedState.sceneId);
              
              let savedPosition = null;
              try {
                const readerState = localStorage.getItem(READER_STATE_KEY);
                if (readerState) {
                  const state = JSON.parse(readerState);
                  if (state.bookId === id && state.chapter === savedState.chapter && state.sceneId === savedState.sceneId && state.audioTime) {
                    savedPosition = state.audioTime;
                  }
                }
              } catch (error) {
                console.error("Error getting saved audio position:", error);
              }
              
              if (hasAudio) {
                preloadAudio(savedState.chapter, savedState.sceneId, savedPosition);
              }
            } else {
              console.log("No saved state found, starting from beginning");
              loadSceneFromHook('Chapter 1', '0');
              
              // Preload audio for the first scene (no saved position)
              if (hasAudio) {
                preloadAudio('Chapter 1', '0', null);
              }
            }
          } catch (error) {
            console.error("Error loading saved state:", error);
            loadSceneFromHook('Chapter 1', '0');
            
            if (hasAudio) {
              preloadAudio('Chapter 1', '0', null);
            }
          }
        }
        
        const loadTime = Math.round(performance.now() - startTime);
        trackTiming('Story Load', 'Initial Load', loadTime);
      }
    };

    initializeStory();
  }, [bookData, loadSceneFromHook, id, title, audioRef, setIsPlaying, location.hash, navigate, hasAudio, preloadAudio]);

  useEffect(() => {
    if (currentScene && currentScene.sceneId) {
      const newUrl = `/story/${id}#${currentScene.sceneId}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [currentScene, id]);

  const handleSave = () => {
    if (!currentScene || !currentScene.chapterName) return;
    
    const storyState = saveStoryState(
      id,
      currentScene.chapterName,
      currentScene.sceneId
    );
    
    if (storyState) {
      setSavedSceneId(storyState.sceneId);
    }
  };

  const handleResume = () => {
    const storyId = id || 'default';
    const savedState = loadSavedState(storyId);
    
    if (savedState) {
      try {
        // Stop current audio if playing
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
        
        loadSceneFromHook(savedState.chapter, savedState.sceneId);
        
        let savedPosition = null;
        try {
          const readerState = localStorage.getItem(READER_STATE_KEY);
          if (readerState) {
            const state = JSON.parse(readerState);
            if (state.bookId === id && state.chapter === savedState.chapter && state.sceneId === savedState.sceneId && state.audioTime) {
              savedPosition = state.audioTime;
            }
          }
        } catch (error) {
          console.error("Error getting saved audio position:", error);
        }
        
        if (hasAudio) {
          preloadAudio(savedState.chapter, savedState.sceneId, savedPosition);
        }
        
        setSavedSceneId(null);
      } catch (error) {
        console.error('Error resuming saved state:', error);
        localStorage.removeItem(`storyState_${storyId}`);
      }
    }
  };

  const toggleSidebar = () => {
    setToggleSidebar(prev => !prev);
  };

  const handleLinkClick = (e, targetSceneId) => {
    e.preventDefault();
    if (currentScene && currentScene.chapterName) {
      loadSceneFromHook(currentScene.chapterName, targetSceneId);
      if (hasAudio && audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        
        preloadAudio(currentScene.chapterName, targetSceneId, null);
      }
      // Update URL immediately
      const newUrl = `/story/${id}#${targetSceneId}`;
      window.history.replaceState(null, '', newUrl);
    }
  };

  const jumpToSection = (sectionNumber) => {
    if (currentScene && currentChapter) {
      loadSceneFromHook(currentChapter, sectionNumber);
      if (hasAudio && audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        
        preloadAudio(currentChapter, sectionNumber, null);
      }
      // Update URL immediately
      const newUrl = `/story/${id}#${sectionNumber}`;
      window.history.replaceState(null, '', newUrl);
    }
  };

  useEffect(() => {
    if (hasAudio && currentScene && currentScene.sceneId && !audioInitialized) {
      const audioPath = getAudioPath(
        id,
        currentChapter,
        currentScene.sceneId,
        voicePreference
      );
      
      if (audioPath && audioRef.current) {
        let savedPosition = null;
        try {
          const readerState = localStorage.getItem(READER_STATE_KEY);
          if (readerState) {
            const state = JSON.parse(readerState);
            if (state.bookId === id && state.chapter === currentChapter && state.sceneId === currentScene.sceneId && state.audioTime) {
              savedPosition = state.audioTime;
            }
          }
        } catch (error) {
          console.error("Error getting saved audio position:", error);
        }
        
        audioRef.current.src = audioPath;
        audioRef.current.load();
        
        if (savedPosition !== null && !isNaN(savedPosition)) {
          const handleCanPlay = () => {
            audioRef.current.currentTime = savedPosition;
            audioRef.current.removeEventListener('canplaythrough', handleCanPlay);
          };
          audioRef.current.addEventListener('canplaythrough', handleCanPlay, { once: true });
        }
        
        setAudioInitialized(true);
      }
    }
  }, [currentScene, currentChapter, voicePreference, hasAudio, id, audioInitialized]);

  const loadScene = useCallback((chapterName, sceneId) => {
    trackNavigation(currentScene?.chapterName || 'None', chapterName);
    logEvent('Scene', 'Change', `${chapterName} - Scene ${sceneId}`);
    loadSceneFromHook(chapterName, sceneId);
    
    if (hasAudio) {
      preloadAudio(chapterName, sceneId, null);
    }
  }, [currentScene, loadSceneFromHook, hasAudio, preloadAudio]);

  // Track time spent on each scene
  useEffect(() => {
    const startTime = Date.now();
    return () => {
      if (currentScene) {
        const timeSpent = Date.now() - startTime;
        trackReadingProgress(
          currentScene.chapterName,
          currentScene.sceneId,
          timeSpent
        );
      }
    };
  }, [currentScene]);

  useEffect(() => {
    const handleHashChange = () => {
      if (location.hash && storyData) {
        const sectionNumber = location.hash.replace('#', '');
        if (sectionNumber && !isNaN(parseInt(sectionNumber, 10))) {
          const targetChapter = findSectionChapter(sectionNumber) || currentChapter || 'Chapter 1';
          loadSceneFromHook(targetChapter, sectionNumber);
          if (hasAudio && audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
          }
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [storyData, currentChapter, loadSceneFromHook, hasAudio, audioRef, setIsPlaying, findSectionChapter]);

  useEffect(() => {
    if (currentScene && currentScene.chapterName && currentScene.sceneId) {
      // Auto-save whenever the scene changes
      const storyState = saveStoryState(
        id,
        currentScene.chapterName,
        currentScene.sceneId
      );
      
      if (storyState) {
        setSavedSceneId(storyState.sceneId);
      }
    }
  }, [currentScene, id]);

  return (
    <div className="story-page">
      {!sidebarShow && (
        <Button icon={<Layout/>} onClick={toggleSidebar} className="theme-button theme-button--w100 toggle-sidebar">
          <Text span>Show sidebar</Text> 
        </Button>
      )}

      <SideBar
        loadScene={() => loadScene('Chapter 1', '0')}
        goBack={goBack}
        handleSave={handleSave}
        handleResume={handleResume}
        handleFontSizeChange={handleFontSizeChange}
        handleColumns={handleColumns}
        isTwoColumns={isTwoColumns}
        sidebarShow={sidebarShow}
        toggleSidebar={toggleSidebar}
        savedSceneId={savedSceneId}
        bookTitle={title}
        bookCover={cover}
        currentChapter={currentChapter}
        currentSceneId={currentScene?.sceneId}
        jumpToSection={jumpToSection}
        hasAudio={hasAudio}
        bookId={id}
        storyData={storyData}
      />
      
      <div className="main-content">
        <StoryContainer
          currentScene={currentScene}
          fontSize={fontSize}
          isTwoColumns={isTwoColumns}
          handleLinkClick={handleLinkClick}
        />
        <ContentPanel 
          currentScene={currentScene}
          fontSize={fontSize}
          description={currentScene?.content?.description}
        />
      </div>
    </div>
  );
};

export default StoryPage;
